namespace $ {

	/** A rights-only projection: never fetch or persist a map's content. */
	export class $shm_hitalama_map_access_proof_land extends $giper_baza_land {
		loading() {}
		persisted() { return false }
		sync() { return this }
		/** Detached verification state owns no Yard subscription. The native
		 * destructor sends Free by link, which would unsubscribe the live Land
		 * sharing this link when a temporary proof is collected.
		 */
		destructor() {}
	}

	export class $shm_hitalama_map_access_proof extends $mol_object {

		/** Read stored ACLs without applying, decrypting or trusting map Sands.
		 * Include already verified live grants not yet flushed to the mine.
		 */
		@ $mol_mem_key
		static stored_packet( [ source, stored ]: readonly [ $giper_baza_land, readonly $giper_baza_unit[] ] ) {
			const unique = new Map< string, $giper_baza_unit >()
			for( const unit of [ ...stored, ...source._pass.values(), ...source._gift.values(), ...source._seal_shot.values() ] ) {
				if( unit instanceof this.$.$giper_baza_auth_pass ) unique.set( 'pass:' + unit.lord().str, unit )
				else if( unit instanceof this.$.$giper_baza_unit_gift || unit instanceof this.$.$giper_baza_unit_seal ) {
					unique.set( unit.kind() + ':' + unit.hash().str, unit )
				}
			}
			const signed = new Set< string >()
			for( const unit of unique.values() ) if( unit instanceof this.$.$giper_baza_unit_gift ) {
				signed.add( unit.lord().str + ':' + unit.hash().str )
			}
			// Content-only seals are neither ACL dependencies nor permission
			// evidence. In an old map there can be thousands of them.
			for( const [ key, unit ] of unique ) if( unit instanceof this.$.$giper_baza_unit_seal ) {
				if( !unit.hash_list().some( hash=> signed.has( unit.lord().str + ':' + hash.str ) ) ) unique.delete( key )
			}
			return this.$.$giper_baza_pack.make([
				[ source.link().str, new this.$.$giper_baza_pack_part([ ...unique.values() ]) ],
			]).asArray()
		}

		@ $mol_action
		static stored( source: $giper_baza_land, units: readonly $giper_baza_unit[] ) {
			return this.verify([ source.link(), this.stored_packet([ source, units ]) ])
		}

		/** Only accepted, signed Gifts and their verification dependencies. */
		static pack( land: $giper_baza_land ) {
			const units = new Set< $giper_baza_unit >()
			for( const gift of land._gift.values() ) {
				const seal = land.unit_seal( gift )
				if( !seal ) $mol_fail( new Error( 'Map rights have not been signed yet' ) )
				units.add( gift )
				units.add( seal )
				for( const lord of [ gift.lord(), gift.mate(), seal.lord() ] ) {
					if( !lord.str ) continue
					const pass = land.lord_pass( lord )
					if( !pass ) $mol_fail( new Error( 'Map rights are missing a public key' ) )
					units.add( pass )
				}
			}
			return this.$.$giper_baza_pack.make([
				[ land.link().str, new this.$.$giper_baza_pack_part([ ... units ]) ],
			])
		}

		@ $mol_mem_key
		static parts( packet: Uint8Array< ArrayBuffer > ) {
			return this.$.$giper_baza_pack.from( packet ).parts()
		}

		/** This proves signatures, not freshness: fetch a new master snapshot. */
		@ $mol_action
		static verify( [ link, packet ]: readonly [ $giper_baza_link, Uint8Array< ArrayBuffer > ] ) {
			const parts = this.parts( packet )
			if( parts.length !== 1 || parts[0][0] !== link.str ) {
				$mol_fail( new Error( 'Map rights snapshot does not match the requested map' ) )
			}
			for( const unit of parts[0][1].units ) {
				if( unit instanceof this.$.$giper_baza_auth_pass ) continue
				if( unit instanceof this.$.$giper_baza_unit_gift ) continue
				if( unit instanceof this.$.$giper_baza_unit_seal ) continue
				$mol_fail( new Error( 'Map content is not allowed in a rights snapshot' ) )
			}
			// Reject incomplete/tampered gifts before applying any mutable state.
			// diff_apply() can otherwise have installed earlier units when it
			// discovers a missing seal later in the packet.
			const seals = parts[0][1].units.filter( unit=> unit instanceof this.$.$giper_baza_unit_seal ) as $giper_baza_unit_seal[]
			for( const unit of parts[0][1].units ) {
				if( !( unit instanceof this.$.$giper_baza_unit_gift ) ) continue
				const hash = unit.hash().str
				if( !seals.some( seal=> seal.lord().str === unit.lord().str && seal.hash_list().some( signed=> signed.str === hash ) ) ) {
					$mol_fail( new Error( 'Map rights snapshot contains an unsigned gift' ) )
				}
			}
			const land = new ( this.$.$mol_wire_sync( this.$.$shm_hitalama_map_access_proof_land ) )()
			land.link = ()=> link
			this.$.$mol_wire_sync( land ).diff_apply( parts[0][1].units, 'skip_load' )
			return land
		}

	}

}
